import { CSSResultGroup, html, LitElement, PropertyValues, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { rmaProductRow } from './product-row.style.js'
import { parseRmaProductDTO, RmaProductDTO } from 'components/ordering/rma/api/dtos/rma-product-dto.js'
import { RmaLineDTO } from 'components/ordering/rma/api/dtos/rma-line-dto.js'
import { ReturnDetailsLine } from 'components/ordering/rma/components/return-details/return-details.js'
import { deleteRmaLineContext } from 'components/ordering/rma/components/context/rma-context.js'
import 'components/ordering/rma/components/return-details/return-details.js'

type RowView = {
  imageUrl: string
  productName: string
  productCode: string
  ean: string | null
  originMove: number | null
  shipmentDate: string | null
  saleReference: string | null
  erpOrderNumber: string | null
  rmaAllowed: boolean
  line: ReturnDetailsLine
}

@customElement('csc-rma-product-row')
export class CSCRmaProductRow extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, rmaProductRow]

  @property({ attribute: 'product', type: String })
  public product = ''

  // csc-rma-latest-orders already shows the shipment/order info once, in the
  // order's own accordion header — showing it again per product is redundant.
  @property({ attribute: 'hide-order-meta', type: Boolean })
  public hideOrderMeta = false

  @property({ attribute: false })
  public productData: RmaProductDTO | null = null

  @property({ attribute: false })
  public lineData: RmaLineDTO | null = null

  @state()
  private parsed: RmaProductDTO | null = null

  @consume({ context: deleteRmaLineContext, subscribe: true })
  private deleteLine?: (lineId: string) => Promise<void>

  private onDelete(event: Event) {
    event.stopPropagation()
    const id = this.view?.line.id
    if (id != null) void this.deleteLine?.(id)
  }

  protected override willUpdate(changed: PropertyValues): void {
    super.willUpdate(changed)
    if (changed.has('productData') && this.productData) {
      this.parsed = this.productData
      return
    }
    if (changed.has('product')) {
      try {
        this.parsed = this.product ? parseRmaProductDTO(JSON.parse(this.product)) : null
      } catch (error) {
        console.error('[csc-rma-product-row] failed to parse product attribute:', error)
        this.parsed = null
      }
    }
  }

  private lineFromProduct(product: RmaProductDTO): ReturnDetailsLine {
    return {
      id: null,
      productId: product.productId,
      shippedQuantity: product.shippedQuantity,
      originMove: product.originMove,
      quantity: product.quantity,
      reason: null,
      memo: null,
      memo2: null,
      memo3: null,
      pictures: [],
    }
  }

  private lineFromDraftLine(line: RmaLineDTO): ReturnDetailsLine {
    return {
      id: line.id,
      productId: line.productId,
      shippedQuantity: line.shippedQuantity,
      originMove: line.originMove,
      quantity: line.quantity,
      reason: line.reason,
      memo: line.memo,
      memo2: line.memo2,
      memo3: line.memo3,
      pictures: line.pictures,
    }
  }

  private get view(): RowView | null {
    if (this.lineData) {
      const line = this.lineData
      return {
        imageUrl: line.imageUrl,
        productName: line.productName,
        productCode: line.productCode,
        ean: line.ean,
        originMove: line.originMove,
        shipmentDate: line.shipmentDate,
        saleReference: line.saleReference,
        erpOrderNumber: line.erpOrderNumber,
        rmaAllowed: line.rmaAllowed,
        line: this.lineFromDraftLine(line),
      }
    }
    if (this.parsed) {
      const product = this.parsed
      return {
        imageUrl: product.imageUrl,
        productName: product.productName,
        productCode: product.productCode,
        ean: product.ean,
        originMove: product.originMove,
        shipmentDate: product.shipmentDate,
        saleReference: product.saleReference,
        erpOrderNumber: product.erpOrderNumber,
        rmaAllowed: product.rmaAllowed,
        line: this.lineFromProduct(product),
      }
    }
    return null
  }

  private renderShipmentColumn(label: ReturnType<typeof t>, value: string | null) {
    if (!value) return nothing
    return html`
      <cse-caption-description class="rma-product-row__shipment-column">
        <span slot="caption">${label}</span>
        ${value}
      </cse-caption-description>
    `
  }

  private renderShipment(view: RowView) {
    return html`
      <cse-paper variant="secondary" data-test="rma-product-row-shipment">
        <div class="rma-product-row__shipment">
          ${this.renderShipmentColumn(t('delivery_date', { ns: 'csc-rma-product-row', default: 'Delivery date' }), view.shipmentDate)}
          ${this.renderShipmentColumn(t('order_number', { ns: 'csc-rma-product-row', default: 'Order number' }), view.saleReference)}
          ${this.renderShipmentColumn(t('erp_number', { ns: 'csc-rma-product-row', default: 'ERP number' }), view.erpOrderNumber)}
        </div>
      </cse-paper>
    `
  }

  override render() {
    const view = this.view
    if (!view) return html`<div class="rma-product-row" data-test="rma-product-row"></div>`
    return html`
      <cse-accordion
        class="rma-product-row"
        data-test="rma-product-row"
        size="lg"
        @accordion-toggle=${(event: Event) => event.stopPropagation()}
      >
        <div slot="title" class="rma-product-row__heading">
          <div class="rma-product-row__header">
            <cse-image
              class="rma-product-row__image"
              source=${view.imageUrl}
              alt=${view.productName}
              fit="contain"
              width="75"
              height="56"
            ></cse-image>
            <div class="rma-product-row__info">
              <cse-text as="h3" variant="product-title-m">${view.productName}</cse-text>
              <cse-text variant="caption-m">
                <span class="rma-product-row__meta-label"
                  >${t('product_number', { ns: 'csc-rma-product-row', default: 'Product number:' })}</span
                >
                ${view.productCode}${view.ean
                  ? html` |
                    <span class="rma-product-row__meta-label"
                      >${t('ean', { ns: 'csc-rma-product-row', default: 'EAN:' })}</span
                    >
                    ${view.ean}`
                  : nothing}
              </cse-text>
            </div>
            ${view.line.id != null
              ? html`<cse-icon-button
                  data-test="rma-product-row-delete"
                  class="rma-product-row__delete"
                  variant="ghost"
                  aria-label=${t('delete', { ns: 'csc-rma-product-row', default: 'Remove product' })}
                  @click=${(event: Event) => this.onDelete(event)}
                  @mousedown=${(event: Event) => event.stopPropagation()}
                >
                  <cse-icon icon-name="trash-can" icon-style="light" aria-hidden="true"></cse-icon>
                </cse-icon-button>`
              : nothing}
          </div>
          ${view.originMove && !this.hideOrderMeta ? this.renderShipment(view) : nothing}
        </div>
        <div slot="content" class="rma-product-row__content">
          ${view.rmaAllowed
            ? html`<csc-rma-return-details line=${JSON.stringify(view.line)}></csc-rma-return-details>`
            : html`<cse-divider orientation="horizontal"></cse-divider>
              <cse-text variant="caption-m" data-test="rma-product-row-not-allowed">
                ${t('not_allowed', { ns: 'csc-rma-product-row', default: 'This product cannot be returned' })}
              </cse-text>`}
        </div>
      </cse-accordion>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-product-row': CSCRmaProductRow
  }
}
